/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { makeAutoObservable } from "mobx"

import type { CurrentUser } from "./session-types"

export class SessionStore {
    currentUser: CurrentUser | null = null
    // One set a day keeps duplicates away.
    favApps: Set<number> = new Set<number>()
    enabledApps: Set<number> = new Set<number>()

    constructor() {
        makeAutoObservable(this, {
            setCurrentUser: true,
            removeCurrentUser: true,
            toggleFavApp: true
        })
    }

    setCurrentUser(user: CurrentUser): void {
        this.currentUser = user
        this.favApps = new Set(user.preferences?.favApps ?? [])
        this.enabledApps = new Set(user.preferences?.enabledApps ?? [])
    }

    removeCurrentUser(): void {
        this.currentUser = null
        this.favApps = new Set<number>()
        this.enabledApps = new Set<number>()
    }

    isFavApp(appId: number | null): boolean {
        if (!appId) return false
        return this.favApps.has(appId)
    }

    isEnabledApp(appId: number | null): boolean {
        if (!appId) return false
        return this.enabledApps.has(appId)
    }

    get favAppsArray(): number[] {
        return Array.from(this.favApps)
    }

    get enabledAppsArray(): number[] {
        return Array.from(this.enabledApps)
    }

    toggleFavApp(appId: number | null): void {
        if (!appId) return
        if (this.isFavApp(appId)) {
            this.favApps.delete(appId)
        } else {
            this.favApps.add(appId)
        }
    }

    toggleEnabledApp(appId: number | null): void {
        if (!appId) return
        if (this.isEnabledApp(appId)) {
            this.enabledApps.delete(appId)
        } else {
            this.enabledApps.add(appId)
        }
    }
}
